import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFirestore, collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, doc, setDoc, limit, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAllChildren, getUserProfile } from '../services/firestore';
import { debounce } from '../utils/performance';
import LazyImage from '../components/common/LazyImage';
import { useMessaging } from '../contexts/MessagingContext';
import '../styles/components/spotlight.css';

// Add CSS for dropdown
const dropdownCSS = `
  .show {
    display: block !important;
  }
`;

// TODO: Replace with Firestore integration
const mockPostings = [];

function calculateAge(dob) {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function CreatePostingModal({ open, onClose, onCreated }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [errorPost, setErrorPost] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const fileInputRef = React.useRef();
  const { currentUser } = useAuth();
  const WORD_LIMIT = 500;

  useEffect(() => {
    if (!open) {
      setText('');
      setImage(null);
      setErrorPost(null);
      setCharCount(0);
    }
  }, [open]);

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= WORD_LIMIT) {
      setText(value);
      setCharCount(value.length);
    }
  };

  const handleImageIconClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      setErrorPost('Text is required.');
      return;
    }
    if (text.length > WORD_LIMIT) {
      setErrorPost(`Text exceeds the ${WORD_LIMIT} character limit.`);
      return;
    }
    setLoadingPost(true);
    setErrorPost(null);
    try {
      const db = getFirestore();
      let imageUrl = '';
      if (image) {
        const storage = getStorage();
        const imgRef = storageRef(storage, `spotlightImages/${currentUser.uid}_${Date.now()}_${image.name}`);
        await uploadBytes(imgRef, image);
        imageUrl = await getDownloadURL(imgRef);
      }
      const docData = {
        text,
        imageUrl,
        createdAt: serverTimestamp(),
        userId: currentUser?.uid || null,
        displayName: currentUser?.displayName || '',
      };
      await addDoc(collection(db, 'spotlightPosts'), docData);
      setLoadingPost(false);
      onCreated && onCreated();
      onClose();
    } catch (err) {
      setErrorPost('Failed to create post.');
      setLoadingPost(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.25)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <form onSubmit={handleSubmit} style={{
        background: 'white',
        borderRadius: 20,
        padding: 32,
        minWidth: 350,
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 18
      }}>
        <button type="button" onClick={onClose} style={{
          position: 'absolute',
          top: 16, right: 16,
          background: 'none',
          border: 'none',
          fontSize: 22,
          color: '#8c9bab',
          cursor: 'pointer'
        }}>×</button>
        <h2 style={{ marginBottom: 10, color: 'var(--primary)', fontWeight: 700 }}>Put on Spotlight</h2>
        <div style={{ marginBottom: 0 }}>
          <label style={{ fontWeight: 600, color: '#2d3142', marginBottom: 6, display: 'block' }}>Text</label>
          <textarea
            value={text}
            onChange={handleTextChange}
            required
            maxLength={WORD_LIMIT}
            placeholder="What's on your mind?"
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '16px',
              border: '1.5px solid #e0e4e9',
              minHeight: 80,
              fontSize: 17,
              outline: 'none',
              boxSizing: 'border-box',
              background: '#f9f9fb',
              color: '#2d3142',
              transition: 'border 0.2s',
              marginBottom: 4
            }}
            onFocus={e => e.target.style.border = '1.5px solid var(--primary)'}
            onBlur={e => e.target.style.border = '1.5px solid #e0e4e9'}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
            <span style={{ fontSize: 13, color: charCount > WORD_LIMIT - 20 ? '#ff6b6b' : '#8c9bab' }}>{charCount}/{WORD_LIMIT}</span>
            <span style={{ fontSize: 13, color: '#8c9bab' }}>Max {WORD_LIMIT} chars</span>
        </div>
        </div>
        <div style={{ marginBottom: 0, marginTop: 8 }}>
          <label style={{ fontWeight: 600, color: '#2d3142', marginBottom: 6, display: 'block' }}>Image (optional)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              onClick={handleImageIconClick}
              style={{
                background: '#f9f9fb',
                border: '1.5px solid #e0e4e9',
                borderRadius: '12px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border 0.2s',
              }}
              title="Attach Image"
            >
              {/* Camera/Picture SVG icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="#7367f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
            {image && <span style={{ fontSize: 14, color: '#7367f0' }}>{image.name}</span>}
        </div>
            </div>
        {errorPost && <div style={{ color: 'var(--error)', marginBottom: 8 }}>{errorPost}</div>}
        <button type="submit" disabled={loadingPost} style={{
          width: '100%',
          padding: 12,
          backgroundColor: 'var(--primary)',
          color: 'white',
          border: 'none',
          borderRadius: 12,
          fontWeight: 'bold',
          fontSize: 17,
          marginTop: 8,
          cursor: loadingPost ? 'not-allowed' : 'pointer',
          opacity: loadingPost ? 0.7 : 1,
          boxShadow: '0 2px 8px rgba(115,103,240,0.08)'
        }}>{loadingPost ? 'Posting...' : 'Post'}</button>
      </form>
    </div>
  );
}

const SpotLight = () => {
  const { currentUser, userProfile, isParent, isProvider, isJobSeeker } = useAuth();
  const navigate = useNavigate();
  const { startConversation } = useMessaging();
  const [activeCategory, setActiveCategory] = useState('all');
  const [view, setView] = useState('list');
  const [expandedListing, setExpandedListing] = useState(null);
  const [postings, setPostings] = useState([]);
  const [filteredPostings, setFilteredPostings] = useState([]);
  const [displayedPostings, setDisplayedPostings] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [errorFeed, setErrorFeed] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [cities, setCities] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [filterType, setFilterType] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);



  // Fetch locations (cities) from all users
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const db = getFirestore();
        // Fetch from users collection
        const usersSnap = await getDocs(collection(db, 'users'));
        const userCities = usersSnap.docs
          .map(doc => {
            const data = doc.data();
            // Try to get city from various locations in the user data
            let city = null;
            
            // Check if location is a string that might contain a city
            if (typeof data.location === 'string' && data.location.trim()) {
              city = data.location.trim();
            }
            // Check if address is a string that might contain a city
            else if (typeof data.address === 'string' && data.address.trim()) {
              city = data.address.trim();
            }
            // Check address object
            else if (data.address && data.address.city) {
              city = data.address.city;
            }
            // Check profile object
            else if (data.profile) {
              if (data.profile.address && data.profile.address.city) {
                city = data.profile.address.city;
              } else if (typeof data.profile.address === 'string' && data.profile.address.trim()) {
                city = data.profile.address.trim();
              } else if (typeof data.profile.location === 'string' && data.profile.location.trim()) {
                city = data.profile.location.trim();
              }
            }
            
            return city;
          })
          .filter(city => city); // Remove null/undefined
        
        // Fetch from providers collection - same extraction logic
        const providersSnap = await getDocs(collection(db, 'providers'));
        const providerCities = providersSnap.docs
          .map(doc => {
            const data = doc.data();
            return (
              (data.address && data.address.city) || 
              (data.profile && data.profile.address && data.profile.address.city) ||
              (data.location && data.location.city) ||
              (typeof data.location === 'string' ? data.location : null) ||
              (typeof data.address === 'string' ? data.address : null)
            );
          })
          .filter(city => city);
        
        // Fetch from parents collection
        const parentsSnap = await getDocs(collection(db, 'parents'));
        const parentCities = parentsSnap.docs
          .map(doc => {
            const data = doc.data();
            return (
              (data.address && data.address.city) || 
              (data.profile && data.profile.address && data.profile.address.city) ||
              (data.location && data.location.city) ||
              (typeof data.location === 'string' ? data.location : null) ||
              (typeof data.address === 'string' ? data.address : null)
            );
          })
          .filter(city => city);
        
        // Combine and remove duplicates
        const allCities = [...new Set([...userCities, ...providerCities, ...parentCities])].sort();
        console.log("All cities found:", allCities);
        setCities(allCities);
      } catch (err) {
        console.error("Error fetching cities:", err);
      }
    };
    fetchCities();
  }, []);

  // Fetch and merge all relevant postings
  useEffect(() => {
    const fetchAllPostings = async () => {
      setLoadingFeed(true);
      setErrorFeed(null);
      try {
        const db = getFirestore();
        // Fetch from opportunityPostings
        const oppQ = query(collection(db, 'opportunityPostings'), orderBy('createdAt', 'desc'));
        const oppSnap = await getDocs(oppQ);
        const oppPosts = oppSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'opportunityPostings' }));
        // Fetch from jobs
        const jobsQ = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));
        const jobsSnap = await getDocs(jobsQ);
        const jobs = jobsSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'jobs' }));
        // Fetch from spots
        const spotsQ = query(collection(db, 'spots'), orderBy('createdAt', 'desc'));
        const spotsSnap = await getDocs(spotsQ);
        const spots = spotsSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'spots' }));
        // Fetch from jobseekers
        const seekersQ = query(collection(db, 'jobseekers'), orderBy('createdAt', 'desc'));
        const seekersSnap = await getDocs(seekersQ);
        const seekers = seekersSnap.docs.map(doc => {
          console.log('Jobseeker data:', doc.id, doc.data());
          return { id: doc.id, ...doc.data(), _source: 'jobseekers' };
        });
        
        // Fetch profile updates
        const profileUpdatesQ = query(collection(db, 'profileUpdates'), orderBy('createdAt', 'desc'));
        const profileUpdatesSnap = await getDocs(profileUpdatesQ);
        const profileUpdates = profileUpdatesSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'profileUpdates' }));
        
        // Fetch children data from children collection
        const children = await getAllChildren();
        // Deduplicate children by id
        const uniqueChildren = Array.from(new Map(children.map(child => [child.id, child])).values());
        // Fetch parent profiles for all unique parentUids
        const parentUids = Array.from(new Set(uniqueChildren.map(child => child.parentUid)));
        const parentProfiles = {};
        for (const uid of parentUids) {
          try {
            const parent = await getUserProfile('parents', uid);
            parentProfiles[uid] = parent.profile?.displayName || parent.displayName || parent.email || uid;
          } catch {
            parentProfiles[uid] = uid;
          }
        }
        const seekingChildcarePosts = uniqueChildren.map(child => ({
          id: child.id,
          _source: 'children',
                type: 'seekingChildcare',
          title: `Seeking care for ${child.name}`,
          childName: child.name,
          childAge: calculateAge(child.dateOfBirth),
                ageGroup: child.ageCategory || '',
          parentId: child.parentUid,
          provider: parentProfiles[child.parentUid],
          location: child.address || '',
          description: child.specialNeeds || 'Looking for childcare',
          badges: [ ...(child.specialNeeds ? ['Special Needs'] : []), ...(child.allergies ? ['Allergies'] : []), ...(Array.isArray(child.interests) ? child.interests : []) ],
          createdAt: child.createdAt,
          postedDate: child.createdAt && child.createdAt.toDate ? child.createdAt.toDate().toLocaleDateString() : '',
                gender: child.gender || 'Not specified',
          profile: child,
          userType: 'parent',
        }));
        
        console.log('Seeking childcare posts from children:', seekingChildcarePosts);
        
        // Also fetch regular profile changes
        const usersQ = query(collection(db, 'users'), orderBy('lastUpdated', 'desc'), limit(10));
        const usersSnap = await getDocs(usersQ);
        const userProfiles = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'users' }));
        
        // Create a user lookup map to retrieve display names for any user ID
        const userIdToNameMap = {};
        userProfiles.forEach(user => {
          if (user.id) {
            userIdToNameMap[user.id] = (user.profile && user.profile.displayName) || user.displayName || user.fullName || user.firstName || 'User';
          }
        });
        
        // Helper function to get display name from user ID
        const getUserDisplayName = (userId) => {
          return userId && userIdToNameMap[userId] ? userIdToNameMap[userId] : null;
        };
        
        // Console log all searchers to debug
        console.log('Jobseekers data:', seekers);
        
        // Normalize all
        const normalized = [
          ...oppPosts.map(post => {
            // Always prefer profile.displayName for card display
            const displayName = (post.profile && post.profile.displayName) || post.displayName || post.provider || post.parent || post.fullName || post.name || post.firstName || (post.profile && (post.profile.fullName || post.profile.name || post.profile.firstName)) || getUserDisplayName(post.userId) || (post.title ? post.title.split(' ')[0] : 'User');
            return {
              ...post,
              type: post.type,
              title: post.title,
              provider: displayName,
              location: post.location || '',
              rate: post.rate || '',
              schedule: post.schedule || '',
              urgency: post.urgency || '',
              postedDate: post.postedDate || (post.createdAt && post.createdAt.toDate ? post.createdAt.toDate().toLocaleDateString() : ''),
              description: post.description || '',
              badges: post.badges || [],
              ageGroup: post.ageGroup || '',
              startDate: post.startDate || '',
              experience: post.experience || '',
              availability: post.availability || '',
              userId: post.userId || '',
              userType: post.userType || (post.type === 'jobOpening' ? 'provider' : 'jobseeker'),
            };
          }),
          ...jobs.map(job => {
            // Always prefer profile.displayName for card display (parent jobs need this too)
            // Check for parent name in job.parent first if this is a parent job
            // Log job data to check fields
            console.log('Processing job:', job.id, {
              postedBy: job.postedBy,
              parentId: job.parentId,
              parentUid: job.parentUid,
              createdBy: job.createdBy,
              createdByType: job.createdByType,
              userType: job.userType,
              employer: job.employer
            });
            
            // More comprehensive detection of parent jobs
            const isParentJob = 
              job.postedBy === 'parent' || 
              job.parentId || 
              job.parentUid || 
              job.createdByType === 'parent' || 
              job.userType === 'parent' || 
              job.employer === 'parent' ||
              (job.parent && (typeof job.parent === 'string' || job.parent === true));
            
            // For parent jobs, we need to check for different field paths
            let displayName;
            if (isParentJob) {
              // Log parent job fields to help debug
              console.log('Parent job detected:', job.id, {
                parentProfile: job.parentProfile,
                profile: job.profile,
                parent: job.parent,
                parentUID: job.parentUID || job.parentUid,
                createdBy: job.createdBy
              });
              
              // Enhanced check for parent profile fields
              displayName = 
                // First check directly on parent object if it exists
                (job.parent && job.parent.displayName) ||
                (job.parent && job.parent.profile && job.parent.profile.displayName) ||
                // Then check parentProfile
                (job.parentProfile && job.parentProfile.displayName) || 
                // Check standard profile paths
                (job.profile && job.profile.displayName) || 
                // Direct properties - parent specific first
                job.parentName || job.parentDisplayName || job.employerName || 
                // Other display name properties
                job.displayName || job.postedByName ||
                // Then provider-related fields (fallbacks)
                job.providerName || job.provider || 
                // General name fields
                job.fullName || job.name || job.firstName || 
                // Nested parent names
                (job.parent && job.parent.name) ||
                (job.parent && job.parent.fullName) ||
                // Back to profile paths for nested names
                (job.parentProfile && (job.parentProfile.fullName || job.parentProfile.name || job.parentProfile.firstName)) ||
                (job.profile && (job.profile.fullName || job.profile.name || job.profile.firstName)) || 
                // Try the user ID lookup - try multiple ID fields
                getUserDisplayName(job.parentUID || job.parentUid || job.parentId || job.createdBy || job.userId || job.providerUid) || 
                // Last resort fallbacks
                (job.title ? job.title.split(' ')[0] : 'Parent');
            } else {
              // Original logic for non-parent jobs
              displayName = (job.profile && job.profile.displayName) || job.displayName || job.providerName || job.provider || 
                job.fullName || job.name || job.firstName || 
                (job.profile && (job.profile.fullName || job.profile.name || job.profile.firstName)) || 
                getUserDisplayName(job.providerUid) || 
                (job.title ? job.title.split(' ')[0] : 'Employer');
            }
            return {
              ...job,
              type: 'jobOpening',
              title: job.title,
              provider: displayName,
              location: job.location || '',
              rate: job.hourlyRate ? `$${job.hourlyRate}/hour` : (job.rate || ''),
              schedule: job.schedule ? (typeof job.schedule === 'string' ? job.schedule : Object.keys(job.schedule).join(', ')) : '',
              urgency: job.urgency || '',
              postedDate: job.createdAt && job.createdAt.toDate ? job.createdAt.toDate().toLocaleDateString() : '',
              description: job.description || '',
              badges: job.badges || [],
              ageGroup: job.ageGroup || '',
              startDate: job.startDate || '',
              experience: job.experience || '',
              availability: job.availability || '',
              userId: job.providerUid || '',
              userType: isParentJob ? 'parent' : 'provider', // Set userType based on job type
            };
          }),
          ...spots.map(spot => {            console.log('Spot data:', spot.id, {              provider: spot.provider,              providerName: spot.providerName,              displayName: spot.displayName,              profile: spot.profile,              providerUid: spot.providerUid            });                        let displayName = 'Childcare Provider';                        if (spot.profile && spot.profile.displayName) {              displayName = spot.profile.displayName;            } else if (spot.providerName) {              displayName = spot.providerName;            } else if (typeof spot.provider === 'string') {              displayName = spot.provider;            } else if (spot.displayName) {              displayName = spot.displayName;            } else if (spot.provider && typeof spot.provider === 'object' && spot.provider.displayName) {              displayName = spot.provider.displayName;            } else if (spot.fullName) {              displayName = spot.fullName;            } else if (spot.name) {              displayName = spot.name;            } else if (spot.firstName) {              displayName = spot.firstName;            } else if (spot.profile && spot.profile.fullName) {              displayName = spot.profile.fullName;            } else if (spot.profile && spot.profile.name) {              displayName = spot.profile.name;            } else if (spot.profile && spot.profile.firstName) {              displayName = spot.profile.firstName;            } else if (spot.providerUid && getUserDisplayName(spot.providerUid)) {              displayName = getUserDisplayName(spot.providerUid);            }                        return {              ...spot,              type: 'childcareSpot',              title: spot.title || `${spot.ageCategory || 'Childcare'} Spot Available`,              provider: displayName,              location: spot.location || '',              rate: spot.rate || (spot.monthlyRate ? `$${spot.monthlyRate}/month` : ''),              schedule: spot.schedule || '',              urgency: spot.urgency || '',              postedDate: spot.createdAt && spot.createdAt.toDate ? spot.createdAt.toDate().toLocaleDateString() : '',              description: spot.description || '',              badges: spot.badges || [],              ageGroup: spot.ageCategory || spot.ageGroup || '',              startDate: spot.startDate || '',              experience: '',              availability: '',              userId: spot.providerUid || '',              userType: 'provider'            };          }),
          // Add seekers (caregivers) to the normalized array
          ...seekers.map(seeker => {
            // Always prefer profile.displayName for caregiver display
            const displayName = (seeker.profile && seeker.profile.displayName) || 
                              seeker.displayName || 
                              seeker.fullName || 
                              seeker.name || 
                              seeker.firstName || 
                              (seeker.profile && (seeker.profile.fullName || seeker.profile.name || seeker.profile.firstName)) || 
                              getUserDisplayName(seeker.id) || 
                              'Caregiver';
            
            return {
              ...seeker,
              type: 'nannyAvailable',
              title: displayName ? `${displayName} Available` : 'Caregiver Available',
              provider: displayName,
              location: seeker.address || seeker.location || (seeker.profile && seeker.profile.address) || '',
              rate: seeker.hourlyRate ? `$${seeker.hourlyRate}/hour` : '',
              schedule: '',
              urgency: '',
              postedDate: seeker.createdAt && seeker.createdAt.toDate ? seeker.createdAt.toDate().toLocaleDateString() : '',
              description: seeker.bio || seeker.description || (seeker.profile && seeker.profile.bio) || '',
              badges: seeker.certifications || [],
              ageGroup: '',
              startDate: '',
              experience: seeker.experience || (seeker.profile && seeker.profile.experience) || '',
              availability: seeker.availability ? Object.keys(seeker.availability).filter(day => seeker.availability[day].start).join(', ') : '',
              userId: seeker.id || seeker.uid || (seeker.profile && seeker.profile.id) || '',
              userType: 'jobseeker',
            };
          }),
        ];
        // Then merge in seekingChildcarePosts from children collection only
        const normalizedWithSeekingChildcare = [...normalized, ...seekingChildcarePosts];
        normalizedWithSeekingChildcare.sort((a, b) => {
          let aDate = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : (a.postedDate ? new Date(a.postedDate) : new Date(0));
          let bDate = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : (b.postedDate ? new Date(b.postedDate) : new Date(0));
          return bDate - aDate;
        });
        setPostings(normalizedWithSeekingChildcare);
      } catch (err) {
        setErrorFeed('Failed to load postings.');
      } finally {
        setLoadingFeed(false);
      }
    };
    fetchAllPostings();
  }, []);

  // Memoized search function with debouncing
  const debouncedSearch = useMemo(
    () => debounce((query, postings) => {
      if (!query.trim()) {
        setFilteredPostings(postings);
        return;
      }
      
      const lowerQuery = query.toLowerCase();
      const filtered = postings.filter(posting => {
        const title = (posting.title || '').toLowerCase();
        const description = (posting.description || '').toLowerCase();
        const text = (posting.text || '').toLowerCase();
        const displayName = (posting.displayName || '').toLowerCase();
        const childName = (posting.childName || '').toLowerCase();
        
        return title.includes(lowerQuery) ||
               description.includes(lowerQuery) ||
               text.includes(lowerQuery) ||
               displayName.includes(lowerQuery) ||
               childName.includes(lowerQuery);
      });
      
      setFilteredPostings(filtered);
    }, 300),
    []
  );

  // Update search effect to use debounced function
  useEffect(() => {
    debouncedSearch(searchQuery, postings);
  }, [searchQuery, postings, debouncedSearch]);

  // Optimize sorting with memoization
  const sortedPostings = useMemo(() => {
    const sorted = [...filteredPostings].sort((a, b) => {
      if (sortOption === 'newest') {
        const aDate = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : 
                    (a.lastUpdated && a.lastUpdated.toDate ? a.lastUpdated.toDate() : new Date(0));
        const bDate = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : 
                    (b.lastUpdated && b.lastUpdated.toDate ? b.lastUpdated.toDate() : new Date(0));
        return bDate - aDate;
      } 
      else if (sortOption === 'oldest') {
        const aDate = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : 
                    (a.lastUpdated && a.lastUpdated.toDate ? a.lastUpdated.toDate() : new Date(0));
        const bDate = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : 
                    (b.lastUpdated && b.lastUpdated.toDate ? b.lastUpdated.toDate() : new Date(0));
        return aDate - bDate;
      }
      else if (sortOption === 'a-z') {
        return (a.title || '').localeCompare(b.title || '');
      }
      else if (sortOption === 'z-a') {
        return (b.title || '').localeCompare(a.title || '');
      }
      return 0;
    });
    return sorted;
  }, [filteredPostings, sortOption]);

  // Implement pagination
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * itemsPerPage;
    setDisplayedPostings(sortedPostings.slice(startIndex, endIndex));
  }, [sortedPostings, currentPage, itemsPerPage]);

  // Load more function
  const loadMore = useCallback(() => {
    if (isLoadingMore) return;
    
    setIsLoadingMore(true);
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && displayedPostings.length < sortedPostings.length && !isLoadingMore) {
        loadMore();
      }
    }, options);

    const sentinel = document.querySelector('#scroll-sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [displayedPostings.length, sortedPostings.length, loadMore, isLoadingMore]);

  // Get illustration for the type
  const getIllustration = (type) => {
    if (type === 'jobOpening') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
        </svg>
      );
    } else if (type === 'childcareSpot') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      );
    } else if (type === 'nannyAvailable') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      );
    } else if (type === 'seekingChildcare') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
          <circle cx="12" cy="10" r="3"></circle>
          <path d="M17 7.5a5 5 0 0 0-4-3"></path>
          <path d="M19 10.01L19 10"></path>
          <path d="M5 10.01L5 10"></path>
          <path d="M15 18c1.2-1 2-2.25 2-4"></path>
          <path d="M9 18c-1.2-1-2-2.25-2-4"></path>
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      );
    }
  };

  // Get the type name
  const getTypeName = (type) => {
    switch(type) {
      case 'jobOpening': return 'Job Opening';
      case 'childcareSpot': return 'Available Spot';
      case 'nannyAvailable': return 'Caregiver Available';
      case 'seekingChildcare': return 'Seeking Childcare';
      case 'profileUpdate': return 'Profile Update';
      default: return 'Opportunity';
    }
  };

  // Update renderPostingCard for modern design
  const renderPostingCard = (posting) => {
    const isExpanded = expandedListing === posting.id;
    const colors = getTypeColor(posting.type);
    
    // Special handling for seekingChildcare from parents collection
    const isChildPost = posting.type === 'seekingChildcare' && posting._source === 'parents';
    
    if (posting._source === 'spotlight') {
      return (
        <div key={posting.id} className="spotlight-post-card">
          <div className="spotlight-post-header">{posting.displayName || 'User'} put on Spotlight</div>
          <div className="spotlight-post-text">{posting.text}</div>
          {posting.imageUrl && (
            <LazyImage src={posting.imageUrl} alt="Spotlight" className="spotlight-post-image" />
          )}
          <div className="spotlight-post-meta">Posted just now</div>
        </div>
      );
    }
    
    return (
      <div 
        key={posting.id} 
        className={`spotlight-card ${isExpanded ? 'expanded' : ''}`}
        onClick={() => expandCard(posting.id)}
      >
        <div className="spotlight-card-content">
          {/* Left side with icon */}
          <div className="spotlight-icon-section">
            <div 
              className="spotlight-icon"
              style={{
              backgroundColor: colors.secondary,
              color: colors.primary,
              boxShadow: `0 10px 20px ${colors.secondary}`
              }}
            >
              {getIllustration(posting.type)}
            </div>
            
            {/* Status dot */}
            <div 
              className="spotlight-status-dot"
              style={{ backgroundColor: colors.primary }}
            ></div>
          </div>
          
          {/* Main content */}
          <div className="spotlight-main-content">
            {/* Type label */}
            <div 
              className="spotlight-type-label"
              style={{
              backgroundColor: colors.secondary,
                color: colors.primary
              }}
            >
              {getTypeName(posting.type)}
            </div>
            
            {/* Title */}
            <h3 className="spotlight-title">
              {posting.title}
            </h3>
            
            {/* Info row */}
            <div className="spotlight-info-row">
              {posting.location && (
                <div className="spotlight-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  {posting.location}
                </div>
              )}
              
              {posting.rate && (
                <div className="spotlight-info-item" style={{ fontWeight: '600', color: colors.primary }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  {posting.rate.replace('$', '')}
                </div>
              )}
              
              {(posting.availability || posting.schedule) && (
                <div className="spotlight-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  {posting.schedule || posting.availability}
                </div>
              )}
              
              {isChildPost && posting.gender && (
                <div className="spotlight-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                  </svg>
                  Gender: {posting.gender}
                </div>
              )}
              
              {isChildPost && posting.ageGroup && (
                <div className="spotlight-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4"></path>
                    <path d="M16 2v4"></path>
                    <path d="M8 11h8"></path>
                    <path d="M8 16h8"></path>
                    <path d="M12 6v10"></path>
                    <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                  </svg>
                  Age Group: {posting.ageGroup}
                </div>
              )}
            </div>
            
            {/* By provider + posted when */}
            <div className="spotlight-meta-row">
              <div>
                By {posting.provider}
              </div>
              <div>
                Posted {posting.postedDate}
              </div>
            </div>
          </div>
        </div>
        
        {/* Expanded content */}
        <div className={`spotlight-expanded-content ${!isExpanded ? 'collapsed' : ''}`}>
          <div className="spotlight-divider"></div>
          
          {/* Special expanded content for seeking childcare */}
          {isChildPost && isExpanded && (
            <div className="spotlight-child-details">
              <h4>Child Details</h4>
              <div className="spotlight-child-grid">
                {posting.childName && <div><strong>Name:</strong> {posting.childName}</div>}
                {posting.childAge && <div><strong>Age:</strong> {posting.childAge}</div>}
                {posting.gender && <div><strong>Gender:</strong> {posting.gender}</div>}
                {posting.ageGroup && <div><strong>Age Group:</strong> {posting.ageGroup}</div>}
              </div>
            </div>
          )}
          
          {/* Description */}
          <div className="spotlight-description">
            {posting.description}
          </div>
          
          {/* Badges */}
          {posting.badges && posting.badges.length > 0 && (
            <div className="spotlight-badges">
              {posting.badges.map((badge, index) => (
                <div 
                  key={index} 
                  className="spotlight-badge"
                  style={{
                  backgroundColor: colors.secondary,
                    color: colors.primary
                  }}
                >
                  {badge}
                </div>
              ))}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="spotlight-actions">
            <button 
              className="spotlight-btn spotlight-btn-primary"
              style={{
                backgroundColor: colors.primary,
                boxShadow: `0 10px 20px ${colors.secondary}`
              }}
              onClick={e => { e.stopPropagation(); handleContact(posting); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 3 3 9-3 9 19-9Z"></path>
                <path d="M6 12h16"></path>
              </svg>
              Contact Now
            </button>
            
            {posting.type === 'seekingChildcare' ? (
              <button 
                className="spotlight-btn spotlight-btn-secondary"
                onClick={e => {
                  e.stopPropagation();
                  navigate(`/parent/${posting.parentId || posting.userId}`);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                View Profile
              </button>
            ) : posting.type !== 'seekingChildcare' && (
            <button 
              className="spotlight-btn spotlight-btn-secondary"
              onClick={e => { e.stopPropagation(); handleViewProfile(e, posting); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              View Profile
            </button>
            )}
            
            <button 
              className="spotlight-btn spotlight-btn-outline"
              style={{ backgroundColor: '#f9f9fb', color: colors.primary }}
              onClick={(e) => { e.stopPropagation(); handleShare(posting); }}
              title="Share"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
              Share
            </button>
          </div>
        </div>
      </div>
    );
  };

  // After a posting is created, refetch postings
  const handleCreated = () => {
    // Refetch postings
    setLoadingFeed(true);
    setErrorFeed(null);
    const fetchPostings = async () => {
      try {
        const db = getFirestore();
        const q = query(collection(db, 'opportunityPostings'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPostings(data);
      } catch (err) {
        setErrorFeed('Failed to load postings.');
      } finally {
        setLoadingFeed(false);
      }
    };
    fetchPostings();
  };

  // Get the type color - similar to the mockup
  const getTypeColor = (type) => {
    switch(type) {
      case 'jobOpening': return { primary: '#ff6b6b', secondary: 'rgba(255, 107, 107, 0.1)' };
      case 'childcareSpot': return { primary: '#4ecdc4', secondary: 'rgba(78, 205, 196, 0.1)' };
      case 'nannyAvailable': return { primary: '#7367f0', secondary: 'rgba(115, 103, 240, 0.1)' };
      case 'seekingChildcare': return { primary: '#ffc857', secondary: 'rgba(255, 200, 87, 0.1)' };
      case 'profileUpdate': return { primary: '#6c5ce7', secondary: 'rgba(108, 92, 231, 0.1)' };
      default: return { primary: '#8c9bab', secondary: 'rgba(140, 155, 171, 0.1)' };
    }
  };

  // Get unique locations for the location filter (now we'll use cities list instead)
  const locations = [...new Set(postings.map(posting => posting.location))];

  // Handler for expanding a card
  const expandCard = (id) => {
    setExpandedListing(expandedListing === id ? null : id);
  };

  // Handler for sort option change
  const handleSortChange = (option) => {
    setSortOption(option);
  };

  // Contact: navigate to messaging page with posting's userId
  const handleContact = async (posting) => {
    if (!currentUser) {
      alert('You must be logged in to contact.');
      return;
    }
    // Try all possible fields for parent or provider
    const receiverId = posting.parentUid || posting.parentId || posting.providerUid || posting.userId;
    if (!receiverId) {
      alert('No contact available for this posting.');
      return;
    }
    
    try {
      const conversation = await startConversation(receiverId);
      if (conversation && conversation.id) {
        // Navigate to the specific conversation
        navigate(`/messages/${conversation.id}`);
      } else {
        // Fallback to messages page if conversation creation failed
        navigate('/messages');
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
      alert('Failed to start conversation. Please try again.');
    }
  };

  // View profile handler
  const handleViewProfile = (e, posting) => {
    e.stopPropagation();
    let profilePath = '';
    if (posting.providerUid) {
      profilePath = `/provider/${posting.providerUid}`;
    } else if (posting.parentUid) {
      profilePath = `/parent/${posting.parentUid}`;
    } else if (posting.userId) {
      // Fallback to userId and try to infer type
      if (posting.userType === 'provider' || posting.type === 'childcareSpot' || posting.type === 'jobOpening') {
        profilePath = `/provider/${posting.userId}`;
      } else if (posting.userType === 'jobseeker' || posting.type === 'nannyAvailable') {
        profilePath = `/jobseeker/${posting.userId}`;
      } else if (posting.userType === 'parent' || posting.type === 'seekingChildcare') {
        profilePath = `/parent/${posting.userId}`;
      } else {
        profilePath = `/profile/${posting.userId}`;
      }
    } else {
      alert('No profile available for this user.');
      return;
    }
    navigate(profilePath);
  };



  // Fetch posts function
  const fetchSpotlightPosts = async () => {
    setLoadingFeed(true);
    setErrorFeed(null);
    try {
      const db = getFirestore();
      const q = query(collection(db, 'spotlightPosts'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const spotlightPosts = snap.docs.map(doc => ({ id: doc.id, ...doc.data(), _source: 'spotlight' }));
      spotlightPosts.sort((a, b) => {
        const aDate = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : new Date(0);
        const bDate = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : new Date(0);
        return bDate - aDate;
      });
      setPostings(spotlightPosts);
    } catch (err) {
      setErrorFeed('Failed to load spotlight posts.');
    } finally {
      setLoadingFeed(false);
    }
  };

  // Add the handleShare function to handle sharing via Web Share API or clipboard
  function handleShare(posting) {
    const url = window.location.origin + (posting._source === 'spotlight' ? `/spotlight/${posting.id}` : `/profile/${posting.userId || posting.parentId || posting.providerUid || posting.id}`);
    const shareData = {
      title: posting.title || 'Check this out!',
      text: posting.description || '',
      url,
    };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  }

  return (
    <div className="spotlight-container" style={{ 
      backgroundColor: '#f9f9fb', 
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      <style>{dropdownCSS}</style>
      {/* Feed below */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 var(--spacing-md)' }}>
        {/* ...rest of the feed rendering as before... */}
        {loadingFeed ? (
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#f9f9fb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8c9bab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                </svg>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2d3142',
                margin: '0 0 10px 0'
              }}>Loading opportunities...</h3>
              <p style={{
                fontSize: '17px',
                color: '#8c9bab',
                margin: '0'
              }}>Please wait while we fetch the latest opportunities for you</p>
            </div>
        ) : errorFeed ? (
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#fff0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                  <path d="M12 9v4"></path>
                  <path d="M12 17h.01"></path>
                </svg>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2d3142',
                margin: '0 0 10px 0'
              }}>Error loading opportunities</h3>
              <p style={{
                fontSize: '17px',
                color: '#8c9bab',
                margin: '0'
            }}>{errorFeed}</p>
            </div>
          ) : sortedPostings.length === 0 ? (
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#f9f9fb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8c9bab" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                </svg>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#2d3142',
                margin: '0 0 10px 0'
              }}>No opportunities found</h3>
              <p style={{
                fontSize: '17px',
                color: '#8c9bab',
                margin: '0'
              }}>Try adjusting your filters or check back later for new opportunities</p>
            </div>
          ) : (
            <>
              {displayedPostings.map((posting) => renderPostingCard(posting))}
              
              {displayedPostings.length < sortedPostings.length && (
                <div id="scroll-sentinel" style={{ height: '20px', margin: '20px 0' }}>
                  {isLoadingMore && (
                    <div style={{ textAlign: 'center' }}>
                      <div className="loading-spinner"></div>
                      <p>Loading more...</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
      </div>

      {/* Modals remain the same */}
      <CreatePostingModal 
        open={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
        onCreated={fetchSpotlightPosts}
      />
      
      {/* ... rest of the modals ... */}
    </div>
  );
};

export default SpotLight; 